import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArtForm } from './formularioEmisionArt/useArtForm';

// Importación de sub-componentes (Secciones Modulares)
import SectionDetalleArt from './formularioEmisionArt/SectionDetalleArt';
import SectionVariablesArt from './formularioEmisionArt/SectionVariablesArt';
import SectionCompaniaArt from './formularioEmisionArt/SectionCompaniaArt';
import SectionComercialArt from './formularioEmisionArt/SectionComercialArt';
import SectionValoresArt from './formularioEmisionArt/SectionValoresArt';
import SectionDespachoArt from './formularioEmisionArt/SectionDespachoArt';

/**
 * FormularioArt - Componente conector para la emisión de contratos ART
 * @param {boolean} bypassSuccessScreen - Si es true, omite la pantalla verde de éxito local y notifica al padre directamente (ideal para modales de vinculación).
 */
const FormularioArt = ({ clientData, onSuccess, onCancel, onNewCarga, initialData = null, bypassSuccessScreen = false }) => {
    const navigate = useNavigate();
    const [successData, setSuccessData] = useState(null);

    // Inicializamos el Hook y su callback de éxito
    const { state, actions } = useArtForm(clientData, initialData, (msg, data) => {
        // MEJORA: Si viene con instrucción de bypass, o si detectamos que proviene de una cotización vinculada,
        // o si es una edición, disparamos el onSuccess inmediatamente para cerrar el modal y vincular
        if (bypassSuccessScreen || initialData?.cotizacionId || initialData?.id) {
            if (onSuccess) {
                onSuccess(msg, data);
            }
        } else {
            // Solo seteamos éxito local para mostrar la pantalla verde con botones en flujos stand-alone
            setSuccessData(data);
        }
    });

    /**
     * PANTALLA DE ÉXITO (Solo para flujos stand-alone)
     */
    if (successData) {
        const nombreAsegurado = `${successData.clienteNombre || ''} ${successData.clienteApellido || ''}`.trim();

        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-xl border border-green-100 p-10 max-w-3xl mx-auto mt-10 text-center animate-fade-in transition-all duration-300">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-8 shadow-inner">
                    <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                </div>

                <h2 className="text-3xl font-bold text-slate-800 mb-3">¡Emisión de ART Exitosa!</h2>
                <p className="text-slate-500 text-lg mb-2">
                    El contrato <span className="font-mono font-bold text-indigo-600">#{successData.nContrato}</span> ha sido registrado correctamente.
                </p>
                <p className="text-slate-400 mb-10 italic">
                    Asegurado: <span className="font-bold text-slate-700">{nombreAsegurado || 'S/D'}</span>
                </p>

                <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                    <button 
                        onClick={() => {
                            if (onSuccess) {
                                onSuccess('Emitido', successData);
                            }
                            navigate(`/clientes/${successData.clienteId}/contratos/${successData.id}`);
                        }} 
                        className="flex-1 max-w-[240px] bg-slate-800 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:bg-slate-900 transition active:scale-95 flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Ver Contrato
                    </button>

                    <button 
                        onClick={() => {
                            if (onSuccess) {
                                onSuccess('Emitido', successData);
                            }
                            onNewCarga();
                        }}
                        className="flex-1 max-w-[240px] bg-indigo-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:bg-indigo-700 transition active:scale-95 flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                        Cargar otro contrato
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form 
            onSubmit={(e) => {
                e.preventDefault();
                actions.handleSave(e);
            }} 
            className="space-y-8 animate-fade-in text-left pb-10"
        >
            <SectionDetalleArt state={state} actions={actions} />
            <SectionVariablesArt state={state} actions={actions} />
            <SectionCompaniaArt state={state} actions={actions} />
            <SectionComercialArt state={state} actions={actions} />
            <SectionValoresArt state={state} actions={actions} />
            <SectionDespachoArt state={state} actions={actions} />

            {}
            <div className="flex justify-end gap-4 pt-6 border-t border-slate-100">
                <button 
                    type="button" 
                    onClick={onCancel} 
                    className="px-6 py-3 rounded-xl border border-slate-300 font-bold text-slate-500 hover:bg-slate-50 transition uppercase text-xs tracking-widest"
                >
                    Cancelar
                </button>
                <button 
                    type="submit" 
                    disabled={state.isSubmitting} 
                    className="px-10 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 disabled:bg-indigo-300 transition-all uppercase text-xs tracking-widest active:scale-95 flex items-center gap-2"
                >
                    {state.isSubmitting ? (
                        <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Guardando...
                        </>
                    ) : (
                        initialData?.id ? 'Guardar Cambios' : 'Confirmar Contrato ART'
                    )}
                </button>
            </div>
            <style>{`
                .label-form { display: block; font-size: 0.875rem; font-weight: 700; color: #4b5563; margin-bottom: 0.25rem; text-align: left; }
                .input-round { width: 100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; outline: none; transition: all 0.2s; font-size: 0.95rem; background-color: #fff; height: 46px; text-align: left; }
                .input-round:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
                .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </form>
    );
};

export default FormularioArt;